// src/utils/profileCompletion.ts
import type {
  SubmitApplicationInfo,
  ApplicationV2Extras,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationTypesV2";

type CreatorApplicationV2StateShape = {
  SubmitApplicationInfo: SubmitApplicationInfo;
  v2Extras: ApplicationV2Extras;
  currentStep: number;
};

type FieldRule = {
  id: string;
  weight: number;
  // return true if "completed"
  check: (s: CreatorApplicationV2StateShape) => boolean;
};

const isNonEmpty = (v: unknown) =>
  typeof v === "string" ? v.trim().length > 0 : v != null;

const hasArray = (v: unknown) => Array.isArray(v) && v.length > 0;

const hasAnyValueInObject = (obj: unknown) => {
  if (!obj || typeof obj !== "object") return false;
  return Object.values(obj as Record<string, unknown>).some((v) =>
    isNonEmpty(v),
  );
};

// ✅ You can tune these weights anytime
export const COMPLETION_RULES: FieldRule[] = [
  // Step 2 basics
  {
    id: "fullName",
    weight: 10,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.fullName),
  },
  {
    id: "gender",
    weight: 8,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.gender),
  },
  // { id: "ageGroup", weight: 7, check: (s) => isNonEmpty(s.v2Extras.ageGroup) }, // V2 UI
  {
    id: "bodyType",
    weight: 8,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.bodyType),
  },
  {
    id: "skinTone",
    weight: 8,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.skinTone),
  },
  { id: "profilePhoto", weight: 10, check: (s) => !!s.v2Extras.profilePhoto },

  // Step 3 contact
  {
    id: "phoneNumber",
    weight: 10,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.phoneNumber),
  },
  {
    id: "address",
    weight: 8,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.address),
  },
  {
    id: "country",
    weight: 5,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.country),
  },
  {
    id: "state",
    weight: 5,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.state),
  },
  {
    id: "city",
    weight: 5,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.city),
  },
  { id: "pincode", weight: 5, check: (s) => isNonEmpty(s.v2Extras.pincode) },
  // mapLink optional (small weight)
  { id: "mapLink", weight: 2, check: (s) => isNonEmpty(s.v2Extras.mapLink) },

  // Step 4 portfolio
  {
    id: "skillsMulti",
    weight: 8,
    check: (s) => hasArray(s.v2Extras.skillsMulti),
  },
  {
    id: "profileLinks",
    weight: 4,
    check: (s) => hasAnyValueInObject(s.v2Extras.profileLinks),
  },
  {
    id: "comfortableWith",
    weight: 4,
    check: (s) => hasAnyValueInObject(s.v2Extras.comfortableWith),
  },
  {
    id: "portfolioVideos",
    weight: 10,
    check: (s) => hasArray(s.v2Extras.portfolioVideos),
  },
  {
    id: "availabilityPerWeek",
    weight: 4,
    check: (s) => isNonEmpty(s.v2Extras.availableHoursPerWeek),
  },
  {
    id: "weekendAvailabilityV2",
    weight: 2,
    check: (s) =>
      typeof s.v2Extras.availabilityForWeekendsOrEvenings === "boolean",
  },

  // Step 5 equipment
  { id: "coreSetup", weight: 8, check: (s) => hasArray(s.v2Extras.coreSetup) },
  {
    id: "whoCanAppear",
    weight: 5,
    check: (s) => hasArray(s.v2Extras.whoCanAppear),
  },

  // Old payload language (if you use it)
  {
    id: "language",
    weight: 3,
    check: (s) => isNonEmpty(s.SubmitApplicationInfo.language),
  },
];

export function computeProfileCompletion(
  state: CreatorApplicationV2StateShape,
) {
  const total = COMPLETION_RULES.reduce((sum, r) => sum + r.weight, 0);
  const done = COMPLETION_RULES.reduce(
    (sum, r) => sum + (r.check(state) ? r.weight : 0),
    0,
  );

  const percent =
    total === 0 ? 0 : Math.min(100, Math.round((done / total) * 100));
  return { percent, done, total };
}

export function pickEmoji(percent: number, step?: number) {
  // If you want “per step” feel, keep step influence too:
  if (step === 2) return "🙂";
  if (step === 3) return "😎";
  if (step === 4) return "🤩";
  if (step === 5) return percent >= 95 ? "🥳" : "🚀";

  // Fallback based on percent
  if (percent < 25) return "🙂";
  if (percent < 50) return "😎";
  if (percent < 75) return "🤩";
  if (percent < 95) return "🚀";
  return "🥳";
}
